import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiKey, FiEye, FiEyeOff, FiSave, FiCheck, FiLink,
  FiGlobe, FiStar, FiCopy, FiRadio, FiUsers,
  FiTrendingUp, FiAlertTriangle, FiActivity,
} = FiIcons;

// ── Persistence ───────────────────────────────────────────────────────────────
const STORAGE_KEY = 'em_streaming_integrations';
const load = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; } };

// ── Platform definitions ──────────────────────────────────────────────────────
const PLATFORMS = [
  {
    id: 'youtube',
    label: 'YouTube Live',
    emoji: '▶️',
    tagline: 'World\'s largest live streaming audience',
    color: '#FF0000',
    bg: 'bg-[#FF0000]/10',
    text: 'text-[#FF0000]',
    border: 'border-[#FF0000]/30',
    docsUrl: 'https://console.cloud.google.com/',
    streamKeyPlaceholder: 'xxxx-xxxx-xxxx-xxxx-xxxx',
    rtmpBase: 'rtmp://a.rtmp.youtube.com/live2/',
    fields: [
      { key: 'apiKey',        label: 'API Key',              hint: 'Google Cloud Console → APIs & Services → YouTube Data API v3' },
      { key: 'clientId',      label: 'OAuth Client ID',      hint: 'Google Cloud Console → Credentials → OAuth 2.0 Client IDs'   },
      { key: 'clientSecret',  label: 'OAuth Client Secret',  hint: 'Keep server-side only — used for token exchange'              },
      { key: 'channelId',     label: 'Channel ID',           hint: 'YouTube Studio → Customization → Basic Info → Channel ID'    },
      { key: 'streamKey',     label: 'Default Stream Key',   hint: 'YouTube Studio → Go Live → Stream key (reusable key)'        },
    ],
    webhookEvents: ['liveBroadcast.created', 'liveBroadcast.started', 'liveBroadcast.ended', 'liveChat.message'],
    notes: 'Enable the YouTube Data API v3 and YouTube Live Streaming API in Google Cloud Console. Use OAuth 2.0 with youtube.force-ssl scope. Stream keys can be reused across broadcasts.',
    stats: { viewers: '2.7B+', reach: 'Global', latency: '~15s HLS' },
  },
  {
    id: 'facebook',
    label: 'Facebook Live',
    emoji: '📘',
    tagline: 'Reach your community on Facebook & Instagram',
    color: '#1877F2',
    bg: 'bg-[#1877F2]/10',
    text: 'text-[#1877F2]',
    border: 'border-[#1877F2]/30',
    docsUrl: 'https://developers.facebook.com/',
    streamKeyPlaceholder: 'FB-xxxxxxxxxxxx-x',
    rtmpBase: 'rtmps://live-api-s.facebook.com:443/rtmp/',
    fields: [
      { key: 'appId',         label: 'App ID',               hint: 'Meta for Developers → Your App → App ID'                    },
      { key: 'appSecret',     label: 'App Secret',           hint: 'Meta for Developers → Your App → App Secret'                },
      { key: 'accessToken',   label: 'Page Access Token',    hint: 'Long-lived token with pages_manage_posts & live_video scope' },
      { key: 'pageId',        label: 'Page ID',              hint: 'Facebook Page → About → Page ID (or from Graph API)'        },
      { key: 'streamKey',     label: 'Default Stream Key',   hint: 'Generated per broadcast via /live_videos endpoint'           },
    ],
    webhookEvents: ['live_videos', 'feed', 'mention', 'video_feed'],
    notes: 'Create a Meta Business App with live_video permission. Use a long-lived Page Access Token (60-day) and refresh it programmatically. Stream keys are per-broadcast via the Graph API.',
    stats: { viewers: '3B+', reach: 'Facebook + Instagram', latency: '~5s RTMPS' },
  },
  {
    id: 'instagram',
    label: 'Instagram Live',
    emoji: '📸',
    tagline: 'Visual storytelling for memorial moments',
    color: '#E1306C',
    bg: 'bg-[#E1306C]/10',
    text: 'text-[#E1306C]',
    border: 'border-[#E1306C]/30',
    docsUrl: 'https://developers.facebook.com/docs/instagram-api/',
    streamKeyPlaceholder: 'IG-xxxxxxxxxxxx',
    rtmpBase: 'rtmps://edgetee-upload.facebook.com:443/rtmp/',
    fields: [
      { key: 'appId',           label: 'Meta App ID',           hint: 'Same Meta App used for Facebook — enable Instagram Basic Display' },
      { key: 'appSecret',       label: 'Meta App Secret',       hint: 'Shared secret across the Meta app umbrella'                       },
      { key: 'userAccessToken', label: 'User Access Token',     hint: 'Instagram Graph API token with instagram_basic + live_video scope' },
      { key: 'igUserId',        label: 'Instagram User ID',     hint: 'Numeric IG business/creator account ID from Graph API'            },
      { key: 'streamKey',       label: 'Default Stream Key',    hint: 'Generated via /live_media endpoint per broadcast session'          },
    ],
    webhookEvents: ['live_media', 'comments', 'mentions', 'story_insights'],
    notes: 'Instagram Live requires a Professional (Business or Creator) account. Use the Instagram Graph API /live_media endpoint to create broadcast sessions and obtain RTMP URLs + stream keys.',
    stats: { viewers: '2B+', reach: 'Instagram Stories & Reels', latency: '~5s RTMPS' },
  },
  {
    id: 'twitch',
    label: 'Twitch',
    emoji: '🟣',
    tagline: 'Engaged community live streaming',
    color: '#9146FF',
    bg: 'bg-[#9146FF]/10',
    text: 'text-[#9146FF]',
    border: 'border-[#9146FF]/30',
    docsUrl: 'https://dev.twitch.tv/console',
    streamKeyPlaceholder: 'live_xxxxxxxxxx_xxxxxxxxxxxxxx',
    rtmpBase: 'rtmp://live.twitch.tv/app/',
    fields: [
      { key: 'clientId',      label: 'Client ID',            hint: 'Twitch Developer Console → Applications → Client ID'          },
      { key: 'clientSecret',  label: 'Client Secret',        hint: 'Twitch Developer Console → Applications → New Secret'         },
      { key: 'accessToken',   label: 'OAuth Access Token',   hint: 'User token with channel:manage:broadcast + user:read:email'   },
      { key: 'broadcasterId', label: 'Broadcaster ID',       hint: 'Numeric Twitch User ID of the broadcasting account'           },
      { key: 'streamKey',     label: 'Stream Key',           hint: 'Twitch Dashboard → Settings → Stream → Primary Stream Key'   },
    ],
    webhookEvents: ['stream.online', 'stream.offline', 'channel.follow', 'channel.subscribe'],
    notes: 'Register a Twitch app in the Developer Console. Use the Authorization Code Flow for user tokens. Stream keys are persistent — rotate them in Twitch Dashboard if compromised. EventSub webhooks require HTTPS.',
    stats: { viewers: '140M+', reach: 'Gaming & Communities', latency: '~3s low-latency' },
  },
];

// ── Shared UI primitives ──────────────────────────────────────────────────────
const KeyField = ({ label, hint, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  const isSecret = /secret|token|key/i.test(label);
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="relative">
        <input
          type={isSecret && !visible ? 'password' : 'text'}
          placeholder={hint}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full pl-4 pr-12 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
        />
        {isSecret && (
          <button type="button" onClick={() => setVisible(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors">
            <SafeIcon icon={visible ? FiEyeOff : FiEye} className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-text-muted leading-relaxed">{hint}</p>
    </div>
  );
};

const RtmpDisplay = ({ base, streamKey, platform }) => {
  const [copiedBase, setCopiedBase] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);
  const fullUrl = streamKey ? `${base}${streamKey}` : `${base}<stream-key>`;

  const copy = (text, setter) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <div className="space-y-3">
      <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">RTMP Ingest URL</p>
      <div className="bg-accent-navy/5 border border-accent-navy/10 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">Server URL</p>
            <code className="text-[10px] text-accent-navy break-all">{base}</code>
          </div>
          <button onClick={() => copy(base, setCopiedBase)}
            className={`shrink-0 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1 ${copiedBase ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
            <SafeIcon icon={copiedBase ? FiCheck : FiCopy} className="w-3 h-3" />
            {copiedBase ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="border-t border-accent-navy/10 pt-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">Full Ingest URL</p>
            <code className="text-[10px] text-accent-navy break-all">{fullUrl}</code>
          </div>
          <button onClick={() => copy(fullUrl, setCopiedFull)}
            className={`shrink-0 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1 ${copiedFull ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
            <SafeIcon icon={copiedFull ? FiCheck : FiCopy} className="w-3 h-3" />
            {copiedFull ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WebhookField = ({ label, value, onChange, hint }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="flex gap-2">
        <div className="relative flex-grow">
          <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input type="url" placeholder="https://your-server.com/webhooks/..."
            value={value} onChange={e => onChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
          />
        </div>
        <button type="button"
          onClick={() => { navigator.clipboard.writeText(value).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${copied ? 'bg-green-500 border-green-500 text-white' : 'bg-brand-light border-brand-dark text-text-muted hover:border-accent-gold'}`}>
          <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {hint && <p className="text-[10px] text-text-muted">{hint}</p>}
    </div>
  );
};

const InfoBox = ({ children, color = 'blue', emoji }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`flex items-start gap-3 border rounded-2xl p-4 ${colors[color]}`}>
      <span className="text-base flex-shrink-0">{emoji}</span>
      <p className="text-[10px] leading-relaxed">{children}</p>
    </div>
  );
};

const EventChip = ({ label }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-brand-light rounded-xl border border-brand-dark">
    <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
    <span className="text-[9px] font-mono text-text-muted">{label}</span>
  </div>
);

const StatPill = ({ icon, label, value, color }) => (
  <div className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl border ${color}`}>
    <SafeIcon icon={icon} className="w-3.5 h-3.5 flex-shrink-0" />
    <div>
      <p className="text-[8px] font-black uppercase tracking-widest opacity-60">{label}</p>
      <p className="text-[11px] font-black">{value}</p>
    </div>
  </div>
);

const SaveToast = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[200] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">Settings Saved</p>
          <p className="text-[10px] opacity-80">Streaming platform settings updated.</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Sidebar card ──────────────────────────────────────────────────────────────
const PlatformCard = ({ platform, isActive, isDefault, onSelect, onSetDefault }) => (
  <button onClick={onSelect}
    className={`relative w-full text-left p-5 rounded-[24px] border-2 transition-all ${isActive ? `${platform.border} bg-white shadow-lg` : 'border-brand-dark bg-brand-light hover:border-accent-gold/40'}`}>
    {isDefault && (
      <div className="absolute -top-2 -right-2 bg-accent-gold text-white text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
        <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Default
      </div>
    )}
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 ${platform.bg} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
        {platform.emoji}
      </div>
      <div className="min-w-0">
        <p className={`font-black text-sm ${isActive ? platform.text : 'text-accent-navy'}`}>{platform.label}</p>
        <p className="text-[9px] text-text-muted leading-tight">{platform.tagline}</p>
      </div>
    </div>
    {!isDefault && (
      <button type="button" onClick={e => { e.stopPropagation(); onSetDefault(); }}
        className="text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors flex items-center gap-1">
        <SafeIcon icon={FiStar} className="w-3 h-3" /> Set as Default
      </button>
    )}
  </button>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const StreamingIntegrations = () => {
  const saved = load();

  const [activePlatform, setActivePlatform] = useState('youtube');
  const [defaultPlatform, setDefaultPlatform] = useState(saved.defaultPlatform || 'youtube');
  const [showToast, setShowToast] = useState(false);
  const [keys, setKeys] = useState(saved.keys || {});
  const [webhooks, setWebhooks] = useState(saved.webhooks || {});
  const [enabled, setEnabled] = useState(saved.enabled || {
    youtube: true, facebook: true, instagram: false, twitch: false,
  });
  const [simultaneousStream, setSimultaneousStream] = useState(saved.simultaneousStream ?? false);

  const setKey = (pid, fk, val) =>
    setKeys(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [fk]: val } }));

  const setWebhook = (pid, val) =>
    setWebhooks(prev => ({ ...prev, [pid]: val }));

  const toggleEnabled = pid =>
    setEnabled(prev => ({ ...prev, [pid]: !prev[pid] }));

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ defaultPlatform, keys, webhooks, enabled, simultaneousStream }));
    window.dispatchEvent(new CustomEvent('streaming_integrations_updated', { detail: { defaultPlatform, enabled } }));
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const platform = PLATFORMS.find(p => p.id === activePlatform);
  const platformKeys = keys[activePlatform] || {};
  const enabledCount = Object.values(enabled).filter(Boolean).length;

  return (
    <div className="space-y-8">

      {/* Header banner */}
      <div className="bg-accent-navy text-white rounded-[32px] p-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent-gold rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500 rounded-full -ml-32 -mb-32 blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <SafeIcon icon={FiRadio} className="w-6 h-6 text-accent-gold" />
              <h2 className="text-2xl font-serif">Streaming Platform Integrations</h2>
            </div>
            <p className="text-white/60 text-sm font-light max-w-xl">
              Configure global account credentials for all supported streaming services, manage RTMP endpoints, and select a default broadcast platform.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="text-right">
              <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Active Platforms</p>
              <p className="text-3xl font-serif text-accent-gold">{enabledCount}<span className="text-base text-white/40">/{PLATFORMS.length}</span></p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-right">
              <p className="text-[9px] font-black uppercase tracking-widest text-white/40">Default</p>
              <p className="text-sm font-bold text-accent-gold">{PLATFORMS.find(p => p.id === defaultPlatform)?.label}</p>
            </div>
          </div>
        </div>

        {/* Simultaneous streaming toggle */}
        <div className="relative z-10 mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold">Simultaneous Multi-Platform Streaming</p>
            <p className="text-[11px] text-white/50">Push a single RTMP stream to all enabled platforms at once via a restreaming proxy.</p>
          </div>
          <button type="button" onClick={() => setSimultaneousStream(v => !v)}
            className={`w-14 h-7 rounded-full relative transition-colors duration-300 flex-shrink-0 ${simultaneousStream ? 'bg-accent-gold' : 'bg-white/20'}`}>
            <motion.div
              animate={{ x: simultaneousStream ? 28 : 2 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="absolute top-1.5 w-4 h-4 bg-white rounded-full shadow-sm"
            />
          </button>
        </div>
      </div>

      {/* Status strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PLATFORMS.map(p => (
          <div key={p.id} onClick={() => toggleEnabled(p.id)}
            className={`cursor-pointer p-4 rounded-[20px] border-2 flex items-center gap-3 transition-all ${enabled[p.id] ? `${p.border} bg-white` : 'border-brand-dark bg-brand-light opacity-55'}`}>
            <div className={`w-9 h-9 ${p.bg} rounded-xl flex items-center justify-center text-lg flex-shrink-0`}>{p.emoji}</div>
            <div className="min-w-0">
              <p className="text-[10px] font-black text-accent-navy truncate">{p.label}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className={`w-1.5 h-1.5 rounded-full ${enabled[p.id] ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                <p className={`text-[9px] font-bold ${enabled[p.id] ? 'text-green-600' : 'text-text-muted'}`}>
                  {enabled[p.id] ? 'Live Ready' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">

        {/* Sidebar */}
        <div className="space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted px-1">Select Platform</p>
          {PLATFORMS.map(p => (
            <PlatformCard key={p.id} platform={p}
              isActive={activePlatform === p.id}
              isDefault={defaultPlatform === p.id}
              onSelect={() => setActivePlatform(p.id)}
              onSetDefault={() => setDefaultPlatform(p.id)}
            />
          ))}
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          <motion.div key={activePlatform}
            initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}
            className="space-y-6">

            {/* Platform header */}
            <div className={`rounded-[28px] border-2 ${platform.border} bg-white p-7`}>
              <div className="flex items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${platform.bg} rounded-2xl flex items-center justify-center text-3xl`}>
                    {platform.emoji}
                  </div>
                  <div>
                    <h3 className={`font-serif text-2xl ${platform.text}`}>{platform.label}</h3>
                    <p className="text-[10px] text-text-muted">{platform.tagline}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {defaultPlatform === activePlatform && (
                    <span className="bg-accent-gold/10 text-accent-gold border border-accent-gold/20 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                      <SafeIcon icon={FiStar} className="w-3 h-3" /> Default
                    </span>
                  )}
                  {platform.docsUrl && (
                    <a href={platform.docsUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors border border-brand-dark rounded-xl px-3 py-2">
                      <SafeIcon icon={FiGlobe} className="w-3 h-3" /> Docs
                    </a>
                  )}
                  <button type="button" onClick={() => toggleEnabled(activePlatform)}
                    className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${enabled[activePlatform] ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
                    <motion.div
                      animate={{ x: enabled[activePlatform] ? 24 : 2 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                    />
                  </button>
                </div>
              </div>

              {/* Platform stats */}
              <div className="grid grid-cols-3 gap-3">
                <StatPill icon={FiUsers} label="Audience" value={platform.stats.viewers} color={`${platform.border} ${platform.bg} ${platform.text}`} />
                <StatPill icon={FiGlobe} label="Reach" value={platform.stats.reach} color={`${platform.border} ${platform.bg} ${platform.text}`} />
                <StatPill icon={FiActivity} label="Latency" value={platform.stats.latency} color={`${platform.border} ${platform.bg} ${platform.text}`} />
              </div>
            </div>

            {/* API Credentials */}
            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiKey} className="w-4 h-4 text-accent-navy" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">Account Credentials</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">API keys & access tokens</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {platform.fields.map(f => (
                  <KeyField key={f.key} label={f.label} hint={f.hint}
                    value={platformKeys[f.key] || ''}
                    onChange={val => setKey(activePlatform, f.key, val)}
                  />
                ))}
              </div>

              <InfoBox color="blue" emoji="🔐">{platform.notes}</InfoBox>
            </div>

            {/* RTMP Ingest Info */}
            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiRadio} className="w-4 h-4 text-accent-navy" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">RTMP / Ingest Configuration</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Broadcast server details</p>
                </div>
              </div>

              <RtmpDisplay
                base={platform.rtmpBase}
                streamKey={platformKeys.streamKey || ''}
                platform={platform}
              />

              <InfoBox color="amber" emoji="📡">
                Configure your broadcast software (OBS, Streamlabs, vMix) with the Server URL and Stream Key above. The stream key is sensitive — treat it like a password and rotate it if exposed.
              </InfoBox>
            </div>

            {/* Webhook */}
            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiLink} className="w-4 h-4 text-accent-navy" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">Webhook Endpoint</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Real-time event callbacks</p>
                </div>
              </div>

              <WebhookField
                label={`${platform.label} Webhook URL`}
                value={webhooks[activePlatform] || ''}
                onChange={val => setWebhook(activePlatform, val)}
                hint={`Register this URL in your ${platform.label} developer dashboard to receive real-time stream event notifications.`}
              />

              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">Subscribed Events</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {platform.webhookEvents.map(ev => <EventChip key={ev} label={ev} />)}
                </div>
              </div>

              <InfoBox color="red" emoji="⚠️">
                Webhook endpoints must be publicly accessible over HTTPS. Always verify the platform's signature header before processing any payload to prevent spoofed events.
              </InfoBox>
            </div>

            {/* Default Platform Selector */}
            <div className="bg-white rounded-[28px] border border-brand-dark p-7 space-y-5">
              <div className="flex items-center gap-3 pb-5 border-b border-brand-dark">
                <div className="p-2.5 bg-brand-light rounded-xl">
                  <SafeIcon icon={FiStar} className="w-4 h-4 text-accent-gold" />
                </div>
                <div>
                  <h4 className="font-serif text-accent-navy">Default Streaming Platform</h4>
                  <p className="text-[10px] text-text-muted uppercase tracking-widest font-black">Pre-selected for new memorial live events</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PLATFORMS.map(p => (
                  <button key={p.id} type="button" onClick={() => setDefaultPlatform(p.id)}
                    className={`p-5 rounded-[20px] border-2 text-center transition-all ${defaultPlatform === p.id ? `${p.border} bg-white shadow-md` : 'border-brand-dark bg-brand-light hover:border-accent-gold/40'}`}>
                    <div className="text-2xl mb-2">{p.emoji}</div>
                    <p className={`text-[10px] font-black ${defaultPlatform === p.id ? p.text : 'text-accent-navy'}`}>{p.label}</p>
                    {defaultPlatform === p.id && (
                      <div className="mt-2 flex items-center justify-center gap-1 text-accent-gold text-[8px] font-black uppercase">
                        <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" /> Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <InfoBox color="green" emoji="✅">
                The default platform is pre-filled when memorial owners schedule a live event. They can override this choice per broadcast. Only enabled platforms appear as options.
              </InfoBox>
            </div>

          </motion.div>
        </AnimatePresence>
      </div>

      {/* Save */}
      <div className="flex justify-end pt-2">
        <button onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]">
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Streaming Settings
        </button>
      </div>

      <SaveToast show={showToast} />
    </div>
  );
};

export default StreamingIntegrations;